import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users,
  ShoppingBag,
  Percent,
  LayoutDashboard,
  Search,
  Clock,
  Wallet,
  ListOrdered,
  UserCheck,
  BarChart3,
  FileText,
  Ban,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Heart,
  Store,
  X,
  Shield,
} from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import * as marketplaceApi from '../services/adminMarketplace';
import { authService } from '../services/authService';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  HorizontalBars,
  EmptyState,
  DemoDataBanner,
} from '../components/ui/UIComponents';

// ============================================================================
// DONNÉES DE DÉMONSTRATION — Marketplace (économie entre membres)
// Distinct de la Boutique DSI (produits institutionnels)
// Frontend Admin → Worker Admin → Worker Marketplace
// Aucune route inventée : actions UI préparées, branchement ultérieur
// ============================================================================

const DEMO_VENDORS = [
  { id: 'VEN-01', name: 'Aminata Diallo', shop: 'Diallo Academy', status: 'approved', listings: 4, sales: 28, revenue: 42000, since: '2026-01-12' },
  { id: 'VEN-02', name: 'Kouamé Koffi', shop: 'Koffi Studio', status: 'suspended', listings: 1, sales: 2, revenue: 3000, since: '2026-02-01' },
  { id: 'VEN-03', name: 'Sophie Mensah', shop: 'Mensah Design', status: 'approved', listings: 7, sales: 51, revenue: 91000, since: '2025-11-20' },
  { id: 'VEN-04', name: 'Ibrahim Koné', shop: 'Koné Tech', status: 'approved', listings: 2, sales: 9, revenue: 15000, since: '2026-01-28' },
];

const DEMO_LISTINGS = [
  { id: 'LST-11', title: 'Cours Python avancé', vendor: 'Aminata Diallo', category: 'Formation', price: 9000, status: 'published', sales: 12 },
  { id: 'LST-12', title: 'Template UI SaaS', vendor: 'Sophie Mensah', category: 'Design', price: 15000, status: 'published', sales: 8 },
  { id: 'LST-13', title: 'Pack design Figma', vendor: 'Sophie Mensah', category: 'Design', price: 7000, status: 'pending_review', sales: 0 },
  { id: 'LST-14', title: 'Atelier Git', vendor: 'Aminata Diallo', category: 'Formation', price: 5000, status: 'published', sales: 5 },
  { id: 'LST-15', title: 'Script automation', vendor: 'Ibrahim Koné', category: 'Outils', price: 4000, status: 'draft', sales: 0 },
  { id: 'LST-16', title: 'Kit marketing', vendor: 'Kouamé Koffi', category: 'Business', price: 6000, status: 'suspended', sales: 2 },
  { id: 'LST-17', title: 'Cours Excel pro', vendor: 'Sophie Mensah', category: 'Formation', price: 3500, status: 'rejected', sales: 0 },
];

const DEMO_ORDERS = [
  { id: 'MO-201', buyer: 'Jean Yacé', vendor: 'Aminata Diallo', listing: 'Cours Python avancé', gross: 9000, commission: 900, net: 8100, status: 'paid', at: '2026-03-11' },
  { id: 'MO-202', buyer: 'Fatou Ba', vendor: 'Sophie Mensah', listing: 'Template UI SaaS', gross: 15000, commission: 1500, net: 13500, status: 'paid', at: '2026-03-10' },
  { id: 'MO-203', buyer: 'Marc Ndiaye', vendor: 'Aminata Diallo', listing: 'Atelier Git', gross: 5000, commission: 500, net: 4500, status: 'pending_payment', at: '2026-03-12' },
  { id: 'MO-204', buyer: 'Aïcha Traoré', vendor: 'Sophie Mensah', listing: 'Pack design Figma', gross: 7000, commission: 700, net: 6300, status: 'failed', at: '2026-03-09' },
  { id: 'MO-205', buyer: 'Nadia O.', vendor: 'Ibrahim Koné', listing: 'Script automation', gross: 4000, commission: 400, net: 3600, status: 'refunded', at: '2026-03-08' },
];

const DEMO_TRENDING = [
  { title: 'Template UI SaaS', vendor: 'Sophie Mensah', score: 98 },
  { title: 'Cours Python avancé', vendor: 'Aminata Diallo', score: 91 },
  { title: 'Atelier Git', vendor: 'Aminata Diallo', score: 76 },
];

const DEMO_FAVORITES = [
  { title: 'Template UI SaaS', favorites: 34 },
  { title: 'Cours Python avancé', favorites: 28 },
  { title: 'Pack design Figma', favorites: 15 },
];

const TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: 'vendors', label: 'Vendeurs', icon: Users },
  { id: 'applications', label: 'Candidatures', icon: FileText },
  { id: 'listings', label: 'Listings', icon: ListOrdered },
  { id: 'moderation', label: 'Modération', icon: Shield },
  { id: 'orders', label: 'Commandes', icon: ShoppingBag },
  { id: 'economy', label: 'Économie', icon: Wallet },
  { id: 'stats', label: 'Statistiques', icon: BarChart3 },
  { id: 'trends', label: 'Tendances', icon: TrendingUp },
];

const CHART_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#f43f5e', '#0ea5e9', '#8b5cf6', '#64748b'];

const COMMISSION_BPS_DEMO = 1000; // 10 % — affichage démo, non modifiable ici

function badgeFor(status) {
  const s = (status || '').toLowerCase();
  if (['approved', 'published', 'paid', 'active', 'completed'].includes(s)) return { key: 'ACTIF', label: status };
  if (['pending', 'pending_review', 'pending_payment'].includes(s)) return { key: 'EN_ATTENTE', label: status };
  if (['rejected', 'failed', 'suspended'].includes(s)) return { key: s === 'failed' ? 'ERREUR' : 'INACTIF', label: status };
  if (s === 'refunded' || s === 'draft') return { key: 'EXPIRE', label: status };
  return { key: 'INACTIF', label: status };
}

function canViewMarketplace() {
  const user = authService.getCurrentUser();
  const n = user?.niveau;
  if (n === 1 || n === '1' || (typeof n === 'string' && String(n).toUpperCase() === 'SUPER_ADMIN')) return true;
  const perms = authService.getPermissions() || [];
  return perms.some((p) => {
    const code = typeof p === 'string' ? p : p?.code || p?.permission || '';
    return code === 'MARKETPLACE_VOIR';
  });
}

function canModifySellers() {
  const user = authService.getCurrentUser();
  const n = user?.niveau;
  if (n === 1 || n === '1' || (typeof n === 'string' && String(n).toUpperCase() === 'SUPER_ADMIN')) return true;
  const perms = authService.getPermissions() || [];
  return perms.some((p) => {
    const code = typeof p === 'string' ? p : p?.code || p?.permission || '';
    return code === 'MARKETPLACE_VENDEURS_MODIFIER';
  });
}

function extractApplications(res) {
  if (!res) return [];
  const d = res.data !== undefined ? res.data : res;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.applications)) return d.applications;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(res.applications)) return res.applications;
  if (Array.isArray(res.items)) return res.items;
  return [];
}

function extractPagination(res) {
  const pag = res?.pagination || res?.data?.pagination || {};
  return pag.nextCursor ?? pag.next_cursor ?? null;
}

function applicationId(a) {
  return a?.id || a?.applicationId || a?.application_id || null;
}

export default function Marketplace() {

  const { t } = useI18n();
  const sellersWriteOk = canModifySellers();

  const [tab, setTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [modal, setModal] = useState(null); // { type, payload }
  const [rejectReason, setRejectReason] = useState('');

  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState(null);
  const [appStatusFilter, setAppStatusFilter] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [appNextCursor, setAppNextCursor] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appDetail, setAppDetail] = useState(null);
  const [appDetailLoading, setAppDetailLoading] = useState(false);
  const [appDetailError, setAppDetailError] = useState(null);
  const [appActionBusy, setAppActionBusy] = useState(false);
  const [appActionError, setAppActionError] = useState(null);

  const [toast, setToast] = useState(null);

  const humanErr = (err, fallback) => {
    if (err?.status === 403) return 'Vous n’avez pas l’autorisation d’effectuer cette action.';
    if (err?.status === 404) return 'Ressource introuvable.';
    if (err?.status === 409) return err?.message || 'Conflit : cette candidature a déjà été traitée.';
    if (err?.status === 502 || err?.status === 503) return 'Service Marketplace temporairement indisponible.';
    return err?.message || fallback;
  };

  const loadApplications = useCallback(async ({ cursor = null, append = false } = {}) => {
    setAppsLoading(true);
    setAppsError(null);
    try {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      if (appStatusFilter) params.status = appStatusFilter;
      if (appSearch.trim()) params.search = appSearch.trim();
      const res = await marketplaceApi.getApplications(params);
      if (res && res.success === false) {
        throw Object.assign(new Error(res.error || 'Erreur'), { status: 400, code: res.code });
      }
      const list = extractApplications(res);
      setApplications((prev) => (append ? [...prev, ...list] : list));
      setAppNextCursor(extractPagination(res));
    } catch (err) {
      if (!append) setApplications([]);
      setAppNextCursor(null);
      setAppsError(humanErr(err, 'Impossible de charger les candidatures.'));
    } finally {
      setAppsLoading(false);
    }
  }, [appStatusFilter, appSearch]);

  useEffect(() => {
    if (tab === 'applications') {
      loadApplications({ cursor: null, append: false });
    }
  }, [tab, appStatusFilter, loadApplications]);

  const openApplicationDetail = async (id) => {
    setSelectedApp(id);
    setAppDetail(null);
    setAppDetailError(null);
    setAppDetailLoading(true);
    try {
      const res = await marketplaceApi.getApplication(id);
      if (res && res.success === false) {
        throw Object.assign(new Error(res.error || 'Erreur'), { status: 400, code: res.code });
      }
      const d = res?.data && (res.data.id || res.data.status) ? res.data : res;
      setAppDetail(d);
    } catch (err) {
      setAppDetail(null);
      setAppDetailError(humanErr(err, 'Impossible de charger la candidature.'));
    } finally {
      setAppDetailLoading(false);
    }
  };

  const runApprove = async (id) => {
    setAppActionBusy(true);
    setAppActionError(null);
    try {
      const res = await marketplaceApi.approveApplication(id);
      if (res && res.success === false) {
        throw Object.assign(new Error(res.error || 'Erreur'), { status: 400, code: res.code });
      }
      showToast(res?.data?.already ? 'Candidature déjà approuvée.' : 'Candidature approuvée.');
      setModal(null);
      setSelectedApp(null);
      await loadApplications({ cursor: null, append: false });
    } catch (err) {
      setAppActionError(humanErr(err, 'Échec de l’approbation.'));
    } finally {
      setAppActionBusy(false);
    }
  };

  const runReject = async (id, reason) => {
    if (!reason || !String(reason).trim()) {
      setAppActionError('La raison du refus est obligatoire.');
      return;
    }
    setAppActionBusy(true);
    setAppActionError(null);
    try {
      const res = await marketplaceApi.rejectApplication(id, reason.trim());
      if (res && res.success === false) {
        throw Object.assign(new Error(res.error || 'Erreur'), { status: 400, code: res.code });
      }
      showToast('Candidature refusée.');
      setModal(null);
      setRejectReason('');
      setSelectedApp(null);
      await loadApplications({ cursor: null, append: false });
    } catch (err) {
      setAppActionError(humanErr(err, 'Échec du refus.'));
    } finally {
      setAppActionBusy(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const appStats = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter((a) => a.status === 'PENDING').length;
    const approved = applications.filter((a) => a.status === 'APPROVED').length;
    const rejected = applications.filter((a) => a.status === 'REJECTED').length;
    const suspended = applications.filter((a) => a.status === 'SUSPENDED').length;
    return { total, pending, approved, rejected, suspended };
  }, [applications]);

  const appSegments = useMemo(() => {
    const map = {};
    applications.forEach((a) => {
      const s = a.status || '—';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([label, value], i) => ({
      label,
      value,
      color: ['#4f46e5', '#10b981', '#f59e0b', '#f43f5e', '#0ea5e9'][i % 5],
    }));
  }, [applications]);

  const vendorStats = useMemo(() => {
    const total = DEMO_VENDORS.length;
    const approved = DEMO_VENDORS.filter((v) => v.status === 'approved').length;
    const suspended = DEMO_VENDORS.filter((v) => v.status === 'suspended').length;
    return { total, approved, suspended };
  }, []);

  const listingStats = useMemo(() => {
    const counts = { published: 0, pending_review: 0, draft: 0, rejected: 0, suspended: 0 };
    DEMO_LISTINGS.forEach((l) => {
      if (counts[l.status] !== undefined) counts[l.status] += 1;
    });
    return { total: DEMO_LISTINGS.length, ...counts };
  }, []);

  const orderStats = useMemo(() => {
    const counts = { paid: 0, pending_payment: 0, failed: 0, refunded: 0 };
    DEMO_ORDERS.forEach((o) => {
      if (counts[o.status] !== undefined) counts[o.status] += 1;
    });
    const gross = DEMO_ORDERS.reduce((s, o) => s + o.gross, 0);
    const commission = DEMO_ORDERS.reduce((s, o) => s + o.commission, 0);
    const net = DEMO_ORDERS.reduce((s, o) => s + o.net, 0);
    return { total: DEMO_ORDERS.length, ...counts, gross, commission, net };
  }, []);

  const vendorSegments = useMemo(() => {
    const map = {};
    DEMO_VENDORS.forEach((v) => { map[v.status] = (map[v.status] || 0) + 1; });
    return Object.entries(map).map(([label, value], i) => ({ label, value, color: CHART_COLORS[i % CHART_COLORS.length] }));
  }, []);

  const listingSegments = useMemo(() => {
    const map = {};
    DEMO_LISTINGS.forEach((l) => { map[l.status] = (map[l.status] || 0) + 1; });
    return Object.entries(map).map(([label, value], i) => ({ label, value, color: CHART_COLORS[i % CHART_COLORS.length] }));
  }, []);

  const orderSegments = useMemo(() => {
    const map = {};
    DEMO_ORDERS.forEach((o) => { map[o.status] = (map[o.status] || 0) + 1; });
    return Object.entries(map).map(([label, value], i) => ({ label, value, color: CHART_COLORS[i % CHART_COLORS.length] }));
  }, []);

  const categoryBars = useMemo(() => {
    const map = {};
    DEMO_LISTINGS.forEach((l) => { map[l.category] = (map[l.category] || 0) + 1; });
    return Object.entries(map).map(([label, value]) => ({ label, value, color: 'bg-indigo-500' }));
  }, []);

  const topVendors = useMemo(
    () => [...DEMO_VENDORS].sort((a, b) => b.revenue - a.revenue).slice(0, 5),
    []
  );

  const moderationQueue = useMemo(
    () => DEMO_LISTINGS.filter((l) => ['pending_review', 'suspended'].includes(l.status)),
    []
  );

  const filteredVendors = useMemo(() => {
    return DEMO_VENDORS.filter((v) => {
      const match =
        !search ||
        v.name.toLowerCase().includes(search.toLowerCase()) ||
        v.shop.toLowerCase().includes(search.toLowerCase());
      const st = statusFilter === 'ALL' || v.status === statusFilter;
      return match && st;
    });
  }, [search, statusFilter]);

  const filteredListings = useMemo(() => {
    return DEMO_LISTINGS.filter((l) => {
      const match =
        !search ||
        l.title.toLowerCase().includes(search.toLowerCase()) ||
        l.vendor.toLowerCase().includes(search.toLowerCase());
      const st = statusFilter === 'ALL' || l.status === statusFilter;
      return match && st;
    });
  }, [search, statusFilter]);

  const filteredOrders = useMemo(() => {
    return DEMO_ORDERS.filter((o) => {
      const match =
        !search ||
        o.buyer.toLowerCase().includes(search.toLowerCase()) ||
        o.vendor.toLowerCase().includes(search.toLowerCase()) ||
        o.id.toLowerCase().includes(search.toLowerCase());
      const st = statusFilter === 'ALL' || o.status === statusFilter;
      return match && st;
    });
  }, [search, statusFilter]);

  const openAction = (type, payload) => {
    setRejectReason('');
    setModal({ type, payload });
  };

  const confirmAction = async () => {
    if (!modal) return;
    if (modal.type === 'approve_app') {
      const id = applicationId(modal.payload);
      if (id) await runApprove(id);
      return;
    }
    if (modal.type === 'reject_app') {
      const id = applicationId(modal.payload);
      if (id) await runReject(id, rejectReason);
      return;
    }
    const labels = {
      suspend_vendor: 'Vendeur suspendu (maquette)',
      publish_listing: 'Listing publié (maquette)',
      reject_listing: 'Listing rejeté (maquette)',
      suspend_listing: 'Listing suspendu (maquette)',
    };
    showToast(labels[modal.type] || 'Action enregistrée (démo)');
    setModal(null);
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t("pages.marketplace.title")}
        subtitle={t("pages.marketplace.subtitle")}
        demo
        workerName="Worker Marketplace"
      />

      <div className="flex flex-wrap items-center gap-2">
        <DemoDataBanner workerName="Worker Marketplace" />
        <span className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 px-2 py-1 rounded-full">
          Distinct de la Boutique DSI
        </span>
      </div>

      {toast && (
        <div className="p-3 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-semibold">
          {toast}
        </div>
      )}

      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => { setTab(item.id); setSearch(''); setStatusFilter('ALL'); }}
              className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                tab === item.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} /> {item.labelKey ? t(item.labelKey) : item.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            {[
              { label: 'Vendeurs', value: vendorStats.total, icon: Users },
              { label: 'Candidatures', value: appStats.total, icon: FileText },
              { label: 'Listings', value: listingStats.total, icon: ListOrdered },
              { label: 'Commandes', value: orderStats.total, icon: ShoppingBag },
              { label: 'Volume brut', value: `${orderStats.gross.toLocaleString('fr-FR')} F`, icon: Wallet },
              { label: 'Commissions', value: `${orderStats.commission.toLocaleString('fr-FR')} F`, icon: Percent },
              { label: 'Revenus vendeurs', value: `${orderStats.net.toLocaleString('fr-FR')} F`, icon: Wallet },
              { label: 'Favoris', value: DEMO_FAVORITES.reduce((s, f) => s + f.favorites, 0), icon: Heart },
            ].map((k) => {
              const Icon = k.icon;
              return (
                <div key={k.label} className="dsi-card p-4">
                  <div className="flex items-start justify-between gap-1">
                    <div>
                      <p className="text-[10px] font-semibold uppercase text-slate-500">{k.label}</p>
                      <p className="text-lg font-bold text-slate-900 mt-1 leading-tight">{k.value}</p>
                      <p className="text-[10px] text-amber-700 font-medium mt-1">DÉMO</p>
                    </div>
                    <Icon size={14} className="text-indigo-600 shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Statut des vendeurs</h3>
              <p className="text-xs text-slate-500 mb-3">Approved · Suspended</p>
              <DonutChart segments={vendorSegments} centerValue={vendorStats.total} centerLabel="Vendeurs" />
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Répartition des listings</h3>
              <p className="text-xs text-slate-500 mb-3">Draft · Pending · Published · …</p>
              <DonutChart segments={listingSegments} centerValue={listingStats.total} centerLabel="Listings" />
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Commandes</h3>
              <p className="text-xs text-slate-500 mb-3">Paid · Pending · Failed · Refunded</p>
              <DonutChart segments={orderSegments} centerValue={orderStats.total} centerLabel="Cmd" />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Top vendeurs</h3>
              <div className="space-y-2">
                {topVendors.map((v, i) => (
                  <div key={v.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="w-6 text-xs font-bold text-slate-400">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{v.name}</p>
                      <p className="text-[11px] text-slate-500">{v.shop}</p>
                    </div>
                    <p className="text-sm font-bold text-slate-900">{v.revenue.toLocaleString('fr-FR')} F</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-amber-700 mt-2 font-medium">Classement calculé sur données DÉMO</p>
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Listings par catégorie</h3>
              <HorizontalBars rows={categoryBars} />
            </div>
          </div>

          <div className="dsi-card p-4 text-xs text-slate-500">
            Données temporelles indisponibles — aucune courbe d'activité inventée tant que les dates historiques ne sont pas exposées par le Worker.
          </div>
        </div>
      )}

      {/* VENDORS */}
      {tab === 'vendors' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="dsi-card p-4"><p className="text-[10px] uppercase text-slate-500 font-semibold">Total</p><p className="text-xl font-bold mt-1">{vendorStats.total}</p></div>
            <div className="dsi-card p-4"><p className="text-[10px] uppercase text-slate-500 font-semibold">Actifs</p><p className="text-xl font-bold mt-1">{vendorStats.approved}</p></div>
            <div className="dsi-card p-4"><p className="text-[10px] uppercase text-slate-500 font-semibold">Suspendus</p><p className="text-xl font-bold mt-1">{vendorStats.suspended}</p></div>
          </div>
          <div className="dsi-card p-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input className="dsi-input pl-9" placeholder="Rechercher vendeur ou boutique..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="dsi-input w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="ALL">Tous les statuts</option>
              <option value="approved">Approved</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          {filteredVendors.length === 0 ? (
            <EmptyState title="Aucun vendeur" message="Aucun vendeur ne correspond aux filtres." />
          ) : (
            <div className="dsi-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3">Vendeur</th>
                      <th className="px-4 py-3">Boutique</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3">Listings</th>
                      <th className="px-4 py-3">Ventes</th>
                      <th className="px-4 py-3">Revenus</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredVendors.map((v) => {
                      const b = badgeFor(v.status);
                      return (
                        <tr key={v.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-semibold text-slate-900">{v.name}</td>
                          <td className="px-4 py-3 text-slate-600">{v.shop}</td>
                          <td className="px-4 py-3"><StatusBadge statusKey={b.key} text={b.label} /></td>
                          <td className="px-4 py-3">{v.listings}</td>
                          <td className="px-4 py-3">{v.sales}</td>
                          <td className="px-4 py-3 font-bold">{v.revenue.toLocaleString('fr-FR')} F</td>
                          <td className="px-4 py-3 text-right space-x-1">
                            <button onClick={() => setSelectedVendor(v)} className="dsi-btn-secondary text-[11px] py-1.5">Fiche</button>
                            {v.status !== 'suspended' && (
                              <button onClick={() => openAction('suspend_vendor', v)} className="dsi-btn-secondary text-[11px] py-1.5 text-rose-600">Suspendre</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* APPLICATIONS */}
      {tab === 'applications' && (
        <div className="space-y-4">
          {appsError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex justify-between gap-2">
              <span>{appsError}</span>
              <button type="button" className="underline" onClick={() => loadApplications({ cursor: null, append: false })}>Réessayer</button>
            </div>
          )}
          {appActionError && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold">{appActionError}</div>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <input
              className="dsi-input text-xs max-w-xs"
              placeholder="Recherche"
              value={appSearch}
              onChange={(e) => setAppSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadApplications({ cursor: null, append: false });
              }}
            />
            <select
              className="dsi-input w-auto text-xs"
              value={appStatusFilter}
              onChange={(e) => setAppStatusFilter(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="PENDING">PENDING</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="SUSPENDED">SUSPENDED</option>
            </select>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={appsLoading} onClick={() => loadApplications({ cursor: null, append: false })}>
              Actualiser
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ['Total (page)', appStats.total],
              ['En attente', appStats.pending],
              ['Approuvées', appStats.approved],
              ['Refusées', appStats.rejected],
              ['Suspendues', appStats.suspended],
            ].map(([label, value]) => (
              <div key={label} className="dsi-card p-4">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">{label}</p>
                <p className="text-xl font-bold mt-1">{value}</p>
              </div>
            ))}
          </div>
          {appsLoading && !applications.length ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des candidatures…</div>
          ) : !applications.length && !appsError ? (
            <EmptyState title="Aucune candidature" message="Aucune candidature pour ces critères." onRetry={() => loadApplications({ cursor: null, append: false })} />
          ) : (
            <div className="dsi-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3">Identifiant</th>
                      <th className="px-4 py-3">Candidat / boutique</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {applications.map((a) => {
                      const id = applicationId(a);
                      const name = a.candidate || a.candidateName || a.displayName || a.memberId || '—';
                      const shop = a.shop || a.shopName || a.storeName || '';
                      const at = a.at || a.createdAt || a.created_at || a.submittedAt;
                      const b = badgeFor(a.status);
                      return (
                        <tr key={id} className="hover:bg-slate-50/80">
                          <td className="px-4 py-3 font-mono text-xs">{id}</td>
                          <td className="px-4 py-3">
                            <p className="font-medium">{name}</p>
                            {shop ? <p className="text-xs text-slate-500">{shop}</p> : null}
                          </td>
                          <td className="px-4 py-3"><StatusBadge statusKey={b.key} text={a.status} /></td>
                          <td className="px-4 py-3 text-xs">{at != null ? (typeof at === 'number' ? new Date(at).toLocaleString('fr-FR') : String(at)) : '—'}</td>
                          <td className="px-4 py-3 text-right space-x-1">
                            <button type="button" className="dsi-btn-secondary text-[11px] py-1.5" onClick={() => openApplicationDetail(id)}>Voir</button>
                            {sellersWriteOk && a.status === 'PENDING' && (
                              <>
                                <button type="button" className="dsi-btn-secondary text-[11px] py-1.5 text-emerald-700" onClick={() => openAction('approve_app', a)}>Approuver</button>
                                <button type="button" className="dsi-btn-secondary text-[11px] py-1.5 text-rose-600" onClick={() => openAction('reject_app', a)}>Refuser</button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {appNextCursor && (
                <div className="p-3 border-t flex justify-center">
                  <button type="button" className="dsi-btn-secondary text-xs" disabled={appsLoading} onClick={() => loadApplications({ cursor: appNextCursor, append: true })}>
                    Charger plus
                  </button>
                </div>
              )}
            </div>
          )}

          {selectedApp && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-900/40" onClick={() => !appActionBusy && setSelectedApp(null)} />
              <div className="relative w-full max-w-md dsi-card p-6 space-y-3">
                <div className="flex justify-between items-start">
                  <h3 className="font-bold">Candidature</h3>
                  <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setSelectedApp(null)}>Fermer</button>
                </div>
                {appDetailLoading ? (
                  <p className="text-xs text-slate-500">Chargement…</p>
                ) : appDetailError ? (
                  <p className="text-xs text-rose-700 font-semibold">{appDetailError}</p>
                ) : appDetail ? (
                  <div className="text-sm space-y-2">
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Identifiant</span><br /><span className="font-mono text-xs">{applicationId(appDetail) || selectedApp}</span></p>
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Statut</span><br />{appDetail.status || '—'}</p>
                    <p><span className="text-xs uppercase text-slate-500 font-semibold">Membre</span><br />{appDetail.memberId || appDetail.candidate || appDetail.candidateName || '—'}</p>
                    {(appDetail.shop || appDetail.shopName) && (
                      <p><span className="text-xs uppercase text-slate-500 font-semibold">Boutique</span><br />{appDetail.shop || appDetail.shopName}</p>
                    )}
                    {sellersWriteOk && appDetail.status === 'PENDING' && (
                      <div className="flex gap-2 pt-2">
                        <button type="button" className="dsi-btn-primary text-xs" disabled={appActionBusy} onClick={() => openAction('approve_app', appDetail)}>Approuver</button>
                        <button type="button" className="dsi-btn-secondary text-xs text-rose-600" disabled={appActionBusy} onClick={() => openAction('reject_app', appDetail)}>Refuser</button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}

      {/* LISTINGS */}
      {tab === 'listings' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              ['Total', listingStats.total],
              ['Published', listingStats.published],
              ['Pending', listingStats.pending_review],
              ['Draft', listingStats.draft],
              ['Rejected', listingStats.rejected],
              ['Suspended', listingStats.suspended],
            ].map(([label, value]) => (
              <div key={label} className="dsi-card p-4">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">{label}</p>
                <p className="text-xl font-bold mt-1">{value}</p>
              </div>
            ))}
          </div>
          <div className="dsi-card p-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input className="dsi-input pl-9" placeholder="Produit ou vendeur..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="dsi-input w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="ALL">Tous les statuts</option>
              <option value="published">published</option>
              <option value="pending_review">pending_review</option>
              <option value="draft">draft</option>
              <option value="rejected">rejected</option>
              <option value="suspended">suspended</option>
            </select>
          </div>
          <div className="dsi-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Produit</th>
                    <th className="px-4 py-3">Vendeur</th>
                    <th className="px-4 py-3">Catégorie</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3">Prix</th>
                    <th className="px-4 py-3">Ventes</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredListings.map((l) => {
                    const b = badgeFor(l.status);
                    return (
                      <tr key={l.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold text-slate-900">{l.title}</td>
                        <td className="px-4 py-3 text-slate-600">{l.vendor}</td>
                        <td className="px-4 py-3 text-slate-600">{l.category}</td>
                        <td className="px-4 py-3"><StatusBadge statusKey={b.key} text={b.label} /></td>
                        <td className="px-4 py-3 font-bold">{l.price.toLocaleString('fr-FR')} F</td>
                        <td className="px-4 py-3">{l.sales}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setSelectedListing(l)} className="dsi-btn-secondary text-[11px] py-1.5">Fiche</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODERATION */}
      {tab === 'moderation' && (
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            File de modération — listings en <strong>pending_review</strong> ou <strong>suspended</strong>. Actions préparées pour le contrat Admin (publish / reject / suspend).
          </p>
          {moderationQueue.length === 0 ? (
            <EmptyState title="Aucune modération en attente" message="Aucun listing ne nécessite d'intervention." />
          ) : (
            <div className="space-y-3">
              {moderationQueue.map((l) => {
                const b = badgeFor(l.status);
                return (
                  <div key={l.id} className="dsi-card p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{l.title}</p>
                      <p className="text-xs text-slate-500">{l.vendor} · {l.category} · {l.price.toLocaleString('fr-FR')} F</p>
                      <div className="mt-1"><StatusBadge statusKey={b.key} text={b.label} /></div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => openAction('publish_listing', l)} className="dsi-btn-secondary text-[11px] py-1.5 text-emerald-700">Publier</button>
                      <button onClick={() => openAction('reject_listing', l)} className="dsi-btn-secondary text-[11px] py-1.5 text-rose-600">Rejeter</button>
                      <button onClick={() => openAction('suspend_listing', l)} className="dsi-btn-secondary text-[11px] py-1.5 text-amber-700">Suspendre</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ORDERS */}
      {tab === 'orders' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ['Total', orderStats.total],
              ['Payées', orderStats.paid],
              ['En attente', orderStats.pending_payment],
              ['Échouées', orderStats.failed],
              ['Remboursées', orderStats.refunded],
            ].map(([label, value]) => (
              <div key={label} className="dsi-card p-4">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">{label}</p>
                <p className="text-xl font-bold mt-1">{value}</p>
              </div>
            ))}
          </div>
          <div className="dsi-card p-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input className="dsi-input pl-9" placeholder="Acheteur, vendeur, ID..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="dsi-input w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="ALL">Tous</option>
              <option value="paid">paid</option>
              <option value="pending_payment">pending_payment</option>
              <option value="failed">failed</option>
              <option value="refunded">refunded</option>
            </select>
          </div>
          <div className="dsi-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Commande</th>
                    <th className="px-4 py-3">Acheteur</th>
                    <th className="px-4 py-3">Vendeur</th>
                    <th className="px-4 py-3">Brut</th>
                    <th className="px-4 py-3">Commission</th>
                    <th className="px-4 py-3">Net vendeur</th>
                    <th className="px-4 py-3">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredOrders.map((o) => {
                    const b = badgeFor(o.status);
                    return (
                      <tr key={o.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs text-indigo-600">{o.id}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{o.buyer}</td>
                        <td className="px-4 py-3 text-slate-600">{o.vendor}</td>
                        <td className="px-4 py-3 font-bold">{o.gross.toLocaleString('fr-FR')} F</td>
                        <td className="px-4 py-3 text-emerald-700 font-semibold">{o.commission.toLocaleString('fr-FR')} F</td>
                        <td className="px-4 py-3">{o.net.toLocaleString('fr-FR')} F</td>
                        <td className="px-4 py-3"><StatusBadge statusKey={b.key} text={b.label} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ECONOMY */}
      {tab === 'economy' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="dsi-card p-5">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Volume brut</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{orderStats.gross.toLocaleString('fr-FR')} FCFA</p>
              <p className="text-[10px] text-amber-700 mt-1">DÉMO</p>
            </div>
            <div className="dsi-card p-5">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Commission DSI</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{orderStats.commission.toLocaleString('fr-FR')} FCFA</p>
              <p className="text-[11px] text-slate-500 mt-1">Taux affiché : {COMMISSION_BPS_DEMO / 100} % (BPS {COMMISSION_BPS_DEMO}) — non modifiable ici</p>
            </div>
            <div className="dsi-card p-5">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Revenus vendeurs</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{orderStats.net.toLocaleString('fr-FR')} FCFA</p>
            </div>
          </div>

          <div className="dsi-card p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Flux économique (conceptuel)</h3>
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs font-semibold">
              {['Acheteur', 'Paiement', 'Vente Marketplace', 'Commission DSI', 'Revenu vendeur'].map((step, i) => (
                <React.Fragment key={step}>
                  <span className="px-3 py-2 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-800">{step}</span>
                  {i < 4 && <span className="text-slate-400">→</span>}
                </React.Fragment>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-4 text-center">
              Cette visualisation ne remplace pas les données du Worker Paiement. Aucune modification de solde depuis cette UI.
            </p>
          </div>

          <div className="dsi-card p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Répartition des commandes</h3>
            <DonutChart segments={orderSegments} centerValue={orderStats.total} centerLabel="Cmd" />
          </div>
        </div>
      )}

      {/* STATS */}
      {tab === 'stats' && (
        <div className="space-y-4">
          <DemoDataBanner workerName="Worker Marketplace" />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5"><h3 className="text-sm font-semibold mb-3">Vendeurs</h3><DonutChart segments={vendorSegments} centerValue={vendorStats.total} centerLabel="Vendeurs" /></div>
            <div className="dsi-card p-5"><h3 className="text-sm font-semibold mb-3">Candidatures</h3><DonutChart segments={appSegments} centerValue={appStats.total} centerLabel="Apps" /></div>
            <div className="dsi-card p-5"><h3 className="text-sm font-semibold mb-3">Listings</h3><DonutChart segments={listingSegments} centerValue={listingStats.total} centerLabel="Listings" /></div>
            <div className="dsi-card p-5"><h3 className="text-sm font-semibold mb-3">Commandes</h3><DonutChart segments={orderSegments} centerValue={orderStats.total} centerLabel="Cmd" /></div>
          </div>
          <div className="dsi-card p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Top vendeurs</h3>
            <div className="space-y-2">
              {topVendors.map((v, i) => (
                <div key={v.id} className="flex items-center gap-3">
                  <span className="w-5 text-xs font-bold text-slate-400">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700">{v.name}</span>
                      <span className="font-semibold">{v.revenue.toLocaleString('fr-FR')} F</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.round((v.revenue / topVendors[0].revenue) * 100)}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">
            Données temporelles indisponibles — pas de série inventée. Zone analytics vendeur réservée pour agrégats Admin futurs.
          </div>
        </div>
      )}

      {/* TRENDS */}
      {tab === 'trends' && (
        <div className="space-y-4">
          <DemoDataBanner workerName="Worker Marketplace" />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><TrendingUp size={16} /> Tendances</h3>
              <div className="space-y-2">
                {DEMO_TRENDING.map((t, i) => (
                  <div key={t.title} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-xs font-bold text-slate-400">#{i + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">{t.title}</p>
                      <p className="text-[11px] text-slate-500">{t.vendor}</p>
                    </div>
                    <span className="text-xs font-bold text-indigo-600">score {t.score}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-amber-700 mt-2">Aperçu DÉMO — route trending à brancher</p>
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Heart size={16} /> Favoris</h3>
              <div className="space-y-2">
                {DEMO_FAVORITES.map((f) => (
                  <div key={f.title} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-sm font-semibold text-slate-900">{f.title}</p>
                    <span className="text-xs font-bold text-rose-600">{f.favorites} ♥</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-amber-700 mt-2">DÉMO — si agrégats Admin favoris disponibles ultérieurement</p>
            </div>
          </div>
        </div>
      )}

      {/* Vendor sheet */}
      {selectedVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelectedVendor(null)} />
          <div className="relative w-full max-w-lg dsi-card p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">{selectedVendor.name}</h3>
                <p className="text-xs text-slate-500 flex items-center gap-1"><Store size={12} /> {selectedVendor.shop}</p>
              </div>
              <button onClick={() => setSelectedVendor(null)} className="p-2 rounded-xl hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                ['Statut', selectedVendor.status],
                ['Listings', selectedVendor.listings],
                ['Ventes', selectedVendor.sales],
                ['Revenus', `${selectedVendor.revenue.toLocaleString('fr-FR')} FCFA`],
                ['Depuis', selectedVendor.since],
                ['ID', selectedVendor.id],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">{k}</p>
                  <p className="mt-1 font-medium text-slate-800">{String(v)}</p>
                </div>
              ))}
            </div>
            {selectedVendor.status !== 'suspended' && (
              <button onClick={() => { setSelectedVendor(null); openAction('suspend_vendor', selectedVendor); }} className="dsi-btn-secondary text-xs text-rose-600 w-full">
                <Ban size={14} /> Suspendre le vendeur
              </button>
            )}
          </div>
        </div>
      )}

      {/* Listing sheet */}
      {selectedListing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelectedListing(null)} />
          <div className="relative w-full max-w-lg dsi-card p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">{selectedListing.title}</h3>
                <p className="text-xs text-slate-500">{selectedListing.vendor}</p>
              </div>
              <button onClick={() => setSelectedListing(null)} className="p-2 rounded-xl hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                ['Catégorie', selectedListing.category],
                ['Prix', `${selectedListing.price.toLocaleString('fr-FR')} FCFA`],
                ['Statut', selectedListing.status],
                ['Ventes', selectedListing.sales],
                ['ID', selectedListing.id],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">{k}</p>
                  <p className="mt-1 font-medium text-slate-800">{String(v)}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { setSelectedListing(null); openAction('publish_listing', selectedListing); }} className="dsi-btn-secondary text-xs text-emerald-700">Publier</button>
              <button onClick={() => { setSelectedListing(null); openAction('reject_listing', selectedListing); }} className="dsi-btn-secondary text-xs text-rose-600">Rejeter</button>
              <button onClick={() => { setSelectedListing(null); openAction('suspend_listing', selectedListing); }} className="dsi-btn-secondary text-xs text-amber-700">Suspendre</button>
            </div>
          </div>
        </div>
      )}

      {/* Action confirmation modal */}
      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="text-base font-bold text-slate-900">
              {modal.type === 'approve_app' && 'Approuver la candidature'}
              {modal.type === 'reject_app' && 'Refuser la candidature'}
              {modal.type === 'suspend_vendor' && 'Suspendre le vendeur'}
              {modal.type === 'publish_listing' && 'Publier le listing'}
              {modal.type === 'reject_listing' && 'Rejeter le listing'}
              {modal.type === 'suspend_listing' && 'Suspendre le listing'}
            </h3>
            <p className="text-xs text-slate-600">
              {modal.payload.candidate || modal.payload.name || modal.payload.title}
              {modal.payload.shop ? ` — ${modal.payload.shop}` : ''}
            </p>
            {(modal.type === 'reject_app' || modal.type === 'reject_listing') && (
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">Raison</label>
                <textarea className="dsi-input mt-1 min-h-[80px]" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Raison du refus..." />
              </div>
            )}
            {(modal.type === 'suspend_vendor' || modal.type === 'suspend_listing') && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex gap-2">
                <AlertTriangle size={16} className="shrink-0" />
                Action sensible — confirmation requise. Branchement API non actif (maquette).
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setModal(null)} className="dsi-btn-secondary text-xs">Annuler</button>
              <button onClick={confirmAction} className="dsi-btn-primary text-xs" disabled={appActionBusy}>{appActionBusy ? "…" : "Confirmer"}</button>
            </div>
            <p className="text-[10px] text-slate-400">Aucune route inventée — l'appel réel passera par Worker Admin lorsque le contrat sera branché.</p>
          </div>
        </div>
      )}
    </div>
  );
}
